import { useContext, useMemo } from 'react';
import { UserContext } from '../pages/UserContext';
import {
  ACCESS_LEVEL_ADMIN,
  ACR_EDITOR_BASE_URL,
  ANALYTICS_URL,
  ENTITY_APPROVAL_URL,
  ORGANIZATION_CREATE_URL,
  SUPERUSER_TOOLS_URL,
  VENDOR_OVERVIEW_URL,
} from '../utlities/core.constants';

/**
 * Computes the set of site-wide nav destinations and their visibility/labels
 * based on the current user, so the header and footer nav menus can stay
 * in sync without duplicating the underlying access-control logic.
 */
export default function useSiteNavLinks() {
  const { user, isVendorUser, isAgencyUser, isSuperUser, realIsSuperUser, isImpersonating } = useContext(UserContext);

  const isLoggedIn = Boolean(user);
  const isAdminUser = user?.accessLevel === ACCESS_LEVEL_ADMIN || isSuperUser;
  const showForVendorsLink = !isLoggedIn;
  // The ACR Editor is publicly accessible (anonymous users get Public Mode), so the link is shown
  // to everyone; access-specific behavior is handled inside the editor itself.
  const showAcrEditorLink = true;
  const buyersNavLabel = isLoggedIn ? "Product Listings" : "For Buyers";
  // Top-level SuperUser Tools entry point, hidden while already impersonating (you exit via the
  // overlay, not by re-entering the tool).
  const showSuperUserToolsLink = realIsSuperUser && !isImpersonating;

  const myOrganizationMenuItems = useMemo(() => {
    const items = [];

    // Both vendors and agencies (Buyers) own products, so both get "My Product Listings".
    if (isVendorUser || isAgencyUser) {
      items.push({ label: "My Product Listings", to: VENDOR_OVERVIEW_URL });
    }

    // "Manage User Access" is a cross-org admin surface; hide it while impersonating so a SuperUser
    // can't act on user-access requests with unscoped SuperUser reach under an org's guise.
    if (isAdminUser && !isImpersonating) {
      items.push({ label: "Manage User Access", to: ENTITY_APPROVAL_URL });
    }

    if (isSuperUser) {
      items.push({ label: "Analytics", to: ANALYTICS_URL });
      items.push({ label: "Add Organization", to: ORGANIZATION_CREATE_URL });
    }

    return items;
  }, [isVendorUser, isAgencyUser, isAdminUser, isSuperUser, isImpersonating]);

  const showMyOrganizationLink = myOrganizationMenuItems.length > 0;

  return {
    isLoggedIn,
    isAdminUser,
    buyersNavLabel,
    showForVendorsLink,
    showAcrEditorLink,
    acrEditorUrl: ACR_EDITOR_BASE_URL,
    myOrganizationMenuItems,
    showMyOrganizationLink,
    showSuperUserToolsLink,
    superUserToolsUrl: SUPERUSER_TOOLS_URL,
  };
}
